import { useEffect, useMemo, useState } from "react";
import styles from "./SideBar.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IssueCreditsIcon,
  SideBarFifthIcon,
  SideBarFourthIcon,
  SideBarSecondIcon,
  SideBarSeventhIcon,
  SideBarSixthIcon,
  SideBarThirdIcon,
} from "customIcons";
import { PAGE_URLS } from "@constants/common";
import {
  EnabledNavigation,
  selectProfileData,
} from "@redux/slices/CommonSlice";
import { useAppSelector } from "@redux/hooks";

export const ENABLED_NAVIGATION = {
  credit_upload: "credit_upload",
  credit_reports: "credit_reports",
  saved_orders: "saved_orders",
  user_management: "user_management",
  expiry_reminders: "expiry_reminders",
  reports: "reports",
  wallet_balance: "wallet_balance",
} as const;

/* --------------------------------------------------------------------------
 * Navigation Configuration
 * -------------------------------------------------------------------------- */
/**
 * Navigation items configuration
 * Each item contains:
 * - id: Unique identifier for the navigation item
 * - path: Route path for navigation
 * - icon: React component for the icon
 * - tooltip: Label text shown on hover
 */
const navigationItems = [
  {
    id: "place-order",
    path: PAGE_URLS.ISSUE_CREDITS,
    icon: <IssueCreditsIcon />,
    tooltip: "Issue Credits",
    permissionKey: ENABLED_NAVIGATION?.credit_upload, // ✅ Add permission key matching your API
  },
  {
    id: "wallet",
    path: PAGE_URLS.CREDIT_REPORTS,
    icon: <SideBarSecondIcon />,
    tooltip: "Credit Report",
    permissionKey: ENABLED_NAVIGATION?.credit_reports, // ✅ Add permission key
  },
  {
    id: "drafts",
    path: "/drafts",
    icon: <SideBarThirdIcon />,
    tooltip: "Drafts",
    permissionKey: ENABLED_NAVIGATION?.saved_orders, // ✅ Add permission key
  },
  {
    id: "SendReminder",
    path: PAGE_URLS.SEND_REMINDER,
    icon: <SideBarFourthIcon />,
    tooltip: "Send Reminder",
    permissionKey: ENABLED_NAVIGATION?.expiry_reminders, // ✅ Add permission key
  },
  {
    id: "reports",
    path: "/reports",
    icon: <SideBarFifthIcon />,
    tooltip: "Reports",
    permissionKey: ENABLED_NAVIGATION?.reports, // ✅ Add permission key
  },
  {
    id: "user-management",
    path: PAGE_URLS.USER_MANAGEMENT,
    icon: <SideBarSixthIcon />,
    tooltip: "User Management",
    permissionKey: ENABLED_NAVIGATION?.user_management, // ✅ Add permission key
  },
  {
    id: "settings",
    path: "/settings",
    icon: <SideBarSeventhIcon />,
    tooltip: "Settings",
    permissionKey: "settings", // ✅ Add permission key
  },
];

export default function Sidebar() {
  /* --------------------------------------------------------------------------
   * Local State
   * -------------------------------------------------------------------------- */
  /** Currently hovered navigation item ID for tooltip display */
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  /** Currently active page ID based on route */
  const [activePage, setActivePage] = useState<string | null>(null);

  /* --------------------------------------------------------------------------
   * Hooks
   * -------------------------------------------------------------------------- */
  const navigate = useNavigate();
  const location = useLocation();
  const profileData = useAppSelector(selectProfileData);

  // permission flags
  const enabledNavigation =
    profileData?.enabled_navigation as EnabledNavigation;

  const visibleNavigationItems = useMemo(() => {
    return navigationItems.filter((item) => {
      // If no permission key, show by default
      if (!item.permissionKey || item.permissionKey === "settings") return true;

      // Safely check permissions
      return (
        enabledNavigation?.[item.permissionKey as keyof EnabledNavigation] ===
        true
      );
    });
    // Only recalculate when profile permissions change
  }, [enabledNavigation]);

  /* --------------------------------------------------------------------------
   * Effects
   * -------------------------------------------------------------------------- */
  /**
   * Updates active page based on current route
   * Matches current pathname to navigation items and sets active state
   */
  useEffect(() => {
    const found = visibleNavigationItems.find(
      (n) => n.path === location.pathname,
    );
    setActivePage(found ? found.id : null);
  }, [location.pathname, visibleNavigationItems]);

  /* --------------------------------------------------------------------------
   * Render Helpers
   * -------------------------------------------------------------------------- */
  /**
   * Renders a single navigation item with icon, tooltip, and active state
   *
   * @param {Object} item - Navigation item configuration
   * @returns {JSX.Element} Navigation item with button and tooltip
   */
  const renderItem = (item: (typeof navigationItems)[number]) => (
    <div
      key={item.id}
      className={styles["sidebar__item"]}
      onMouseEnter={() => setShowTooltip(item.id)}
      onMouseLeave={() => setShowTooltip(null)}
    >
      {/* Navigation Button */}
      <div
        className={`${styles["sidebar__btn"]} ${
          activePage === item.id ? styles["sidebar__btn--active"] : ""
        }`}
        role="button"
        aria-pressed={activePage === item.id}
        aria-label={item.tooltip}
        onClick={() => navigate(item.path)}
      >
        {item.icon}
      </div>

      {/* Tooltip - Shown on hover */}
      {showTooltip === item.id && (
        <div className={styles["sidebar__tooltip"]} role="tooltip">
          {item.tooltip}
          {/* Tooltip Arrow */}
          <div className={styles["sidebar__tooltip-arrow"]} aria-hidden="true">
            <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
              <path
                d="M0.832188 6.16781L7 0V16L0.832189 9.83219C-0.179701 8.8203 -0.179703 7.1797 0.832188 6.16781Z"
                fill="black"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );

  /* --------------------------------------------------------------------------
   * Render
   * -------------------------------------------------------------------------- */
  return (
    <div className={styles["sidebar"]}>
      <nav className={styles["sidebar__nav"]} role="navigation">
        {/* Navigation Items */}
        <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
          {visibleNavigationItems.map(renderItem)}
        </div>
      </nav>
    </div>
  );
}
